import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.split";
import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.split";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Section: Cart drawer
 * -----------------------------------------------------------------------------
 * Contains highly coupled scripts to render cart items onto a drawer.
 *
 * @namespace cartDrawer
 */

import * as cart from '@shopify/theme-cart';
import { formatMoney } from '@shopify/theme-currency';
import { getSizedImageUrl } from '@shopify/theme-images';
import { register } from '@shopify/theme-sections';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import cssClasses from '~/helpers/cssClasses';
import { on } from '~/helpers/utils';
import Toggle from '~/components/toggle';
import QuantitySelector from '~/components/quantity-selector';
import quantitySelectorTemplate from '~/components/templates/quantity-selector';
import { misc as icons } from '~/helpers/svg-map';
/**
 * DOM selectors.
 */

const selectors = {
  body: '[js-cart-drawer="body"]',
  container: '[js-cart-drawer="container"]',
  footer: '[js-cart-drawer="footer"]',
  lineItem: '[js-ajax-cart="lineItem"]',
  subtotal: '[js-ajax-cart="subtotal"]',
  quantitySelector: '[js-ajax-cart="quantitySelector"]'
};
/**
 * Attribute and data selectors.
 */

const dataSelectors = {
  ajaxCart: 'js-ajax-cart'
};
/**
 * Create a new drawer instance.
 */

const Drawer = Toggle({
  toggleSelector: 'cartDrawer'
});
/**
 * Instance globals.
 */

let quantitySelectorTimer = {};
/**
 * Register the `cart-drawer` section type.
 */

register('cart-drawer', {
  /**
   * Initialise section.
   */
  init() {
    Drawer.init();
    this.setNodeSelectors();
    this.setRemoveEvents(this.container);
    this.constructQuantitySelectors();
  },

  /**
   * Set cached node selectors.
   */
  setNodeSelectors() {
    this.body = document.querySelector(selectors.body);
    this.container = document.querySelector(selectors.container);
    this.footer = document.querySelector(selectors.footer);
    this.subtotal = document.querySelector(selectors.subtotal);
  },

  /**
   * Set EventBus listeners for each instance.
   */
  setEventBusListeners() {
    Frame.EventBus.listen('Toggle:cartDrawer:open', () => disableBodyScroll(this.body));
    Frame.EventBus.listen('Toggle:cartDrawer:close', () => enableBodyScroll(this.body));
    Frame.EventBus.listen('AjaxCart:updated', response => this.handleDrawerOpen(response));
    Frame.EventBus.listen('AjaxCart:removingItem', item => this.setLoadingState(item));
    Frame.EventBus.listen('AjaxCart:cartEmpty', () => this.setEmptyState());
  },

  /**
   * Instantiate quantity selectors using id.
   * - Callback has a timer to reduce number of calls.
   */
  constructQuantitySelectors() {
    const quantitySelectors = this.container.querySelectorAll(selectors.quantitySelector);
    [...quantitySelectors].forEach(item => {
      QuantitySelector("".concat(item.id), () => {
        window.clearTimeout(quantitySelectorTimer);
        quantitySelectorTimer = window.setTimeout(() => {
          this.handleQuantityUpdate(item);
        }, 250);
      }).setInitState();
    });
  },

  /**
   * Handle quantity updates.
   * @param {HTMLElement} element - Quantity selector node element.
   */
  handleQuantityUpdate(element) {
    this.setRenderingState();
    const data = {
      index: element.closest(selectors.lineItem).dataset.index,
      key: element.closest(selectors.lineItem).dataset.key,
      quantity: parseInt(element.querySelector('input').value, 10)
    };
    cart.updateItem(data.key, {
      quantity: data.quantity
    }).then(() => {
      Frame.EventBus.emit('AjaxCart:itemUpdated');
    }).catch(error => {
      if (typeof response === 'undefined') {
        return;
      }

      throw new Error('Could not update item data', error);
    });
  },

  /**
   * Conditionally handle drawer open.
   * @param {Object} data - Cart.js data.
   */
  handleDrawerOpen(data) {
    if (theme.pageType === 'cart') {
      return;
    }

    if (data.item_count === 0) {
      this.render(data);
      return;
    }

    Drawer.open(this.container);
    disableBodyScroll(this.body);
    this.setRenderingState();
    this.render(data);
  },

  /**
   * Render line items template.
   * @param {Object} data - Cart.js data.
   */
  render(data) {
    this.setActiveState(data);
    this.constructQuantitySelectors();
  },

  /**
  * Set active state.
  * @param {Object} data - Cart.js data.
  */
  setActiveState(data) {
    this.body.classList.remove('ajax-cart__empty');
    this.body.innerHTML = this.getActiveTemplate(data.items);
    this.subtotal.setAttribute('data-price', data.total_price);
    this.subtotal.innerHTML = formatMoney(data.total_price, theme.moneyFormat);
    this.footer.classList.remove(cssClasses.hidden);
    window.setTimeout(() => {
      this.container.classList.remove(cssClasses.loading);
    }, 500);
  },

  /**
   * Set event delegation to remove items on container.
   * @param {HTMLElement} container - The container to apply the remove events on.
   */
  setRemoveEvents(container) {
    on('click', container, event => this.handleRemoveEvent(event));
  },

  /**
   * Handle remove events.
   * @param {Event} event - Click event.
   */
  handleRemoveEvent(event) {
    if (!this.isTargetRemove(event.target)) {
      return;
    }

    event.preventDefault();
    Frame.EventBus.emit('AjaxCart:removingItem', event.target.closest(selectors.lineItem));
    cart.removeItem(event.target.dataset.key).then(response => {
      Frame.EventBus.emit('AjaxCart:itemRemoved', response);
      return response;
    }).catch(error => {
      window.console.error(error);
      return error;
    });
  },

  /**
   * Detect remove target click.
   * @param {HTMLElement} target - Clicked target to check remove attributes against.
   * @returns {Boolean}
   */
  isTargetRemove(target) {
    return typeof target.attributes[dataSelectors.ajaxCart] !== 'undefined' && target.attributes[dataSelectors.ajaxCart].nodeValue === 'remove';
  },

  /**
   * Set loading state.
   * @param {Object} element - DOM line item.
   */
  setLoadingState(element) {
    element.classList.add(cssClasses.removing);
  },

  /**
  * Set empty state.
  */
  setEmptyState() {
    this.body.classList.add('ajax-cart__empty');
    this.body.innerHTML = this.getEmptyTemplate();
    this.footer.classList.add(cssClasses.hidden);
  },

  /**
  * Get cart drawer body template.
  * @param {Object} items - Cart line item object data.
  * @returns {String}
  */
  getActiveTemplate(items) {
    return items.map((item, index) => {
      if (this.isAddOnItem(item)) {
        return '';
      }

      return this.getLineItemTemplate(item, index);
    }).join('');
  },

  /**
   * Get empty cart template.
   * @returns {String}
   */
  getEmptyTemplate() {
    return "<p>".concat(theme.strings.cart.general.empty, "</p>");
  },

  /**
   * Get line item template.
   * @param {Object} item - Cart line item object data.
   * @param {Integer} index - Line item index.
   * @returns {string}
   */
  getLineItemTemplate(item, index) {
    const itemTitleRaw = item.product_title.split('|');
    let itemTitle = item.product_title;

    if (itemTitleRaw.length > 0) {
      itemTitle = itemTitleRaw[0];
    }

    return "\n      <div\n        class=\"ajax-cart__line-item\"\n        data-index=\"".concat(index, "\"\n        data-key=\"").concat(item.key, "\"\n        js-ajax-cart=\"lineItem\"\n      >\n        ").concat(this.getLineItemImage(item), "\n\n        <div class=\"ajax-cart__description\">\n          <div class=\"row no-margin\">\n            <div class=\"col xs8\">\n              <div class=\"ajax-cart__inner\">\n                ").concat(this.isVendorEnabled() ? this.getVendorTitle(item) : '', "\n\n                <a\n                  class=\"ajax-cart__title body-1\"\n                  href=\"").concat(item.url, "\"\n                >\n                  ").concat(itemTitle, "\n                </a>\n\n                ").concat(this.getVariantTitle(item), "\n              </div>\n              <div class=\"ajax-cart__price\">\n                ").concat(this.getLineItemPrice(item), "\n              </div>\n            </div>\n\n            <div class=\"col xs4\">\n              <button\n                class=\"ajax-cart__remove\"\n                tabindex=\"0\"\n                data-key=\"").concat(item.key, "\"\n                js-ajax-cart=\"remove\"\n              >\n                ").concat(icons.close, "\n              </button>\n              <div class=\"ajax-cart__quantity-selector\">\n                ").concat(quantitySelectorTemplate(item, index, 'ajax-cart'), "\n              </div>\n            </div>\n          </div>\n        </div>\n      </div>\n\n      ").concat(this.getPreorderMessage(item), "\n    ");
  },

  /**
   * Get line item image template.
   * @param {Object} item - Variant line item object from cart.js.
   * @returns {String}
   */
  getLineItemImage(item) {
    if (!item.image) {
      return '';
    }

    return "\n      <div class=\"ajax-cart__image responsive-image__wrapper\">\n        <a href=\"".concat(item.url, "\">\n          <img\n            alt=\"").concat(item.title, "\"\n            sizes=\"auto\"\n            src=\"").concat(getSizedImageUrl(item.image, '50x'), "\"\n            srcset=\"\n              ").concat(getSizedImageUrl(item.image, '120x'), " 120w,\n              ").concat(getSizedImageUrl(item.image, '240x'), " 240w,\n              ").concat(getSizedImageUrl(item.image, '320x'), " 320w\n            \"\n          >\n        </a>\n      </div>\n    ");
  },

  /**
   * Get variant price template.
   * - Checks if it's discounted or not.
   * @param {Object} item - Cart line item to get price from.
   * @returns {String}
   */
  getLineItemPrice(item) {
    var _item$properties;

    let lineItemTemplate = "";
    const compareAtPrice = ((_item$properties = item.properties) === null || _item$properties === void 0 ? void 0 : _item$properties.compare_at_price) || 0;

    if (compareAtPrice > item.price) {
      lineItemTemplate += "\n        <span\n          class=\"body-2 ajax-cart__price--compare\"\n          data-compare-at-price=\"".concat(compareAtPrice, "\"\n          js-currency=\"compare_at_price\">\n          ").concat(formatMoney(compareAtPrice, theme.moneyFormat), "\n        </span>\n      ");
    }

    if (item.original_line_price !== item.line_price) {
      if (item.discounted_price !== item.price) {
        lineItemTemplate += "\n          <span class=\"visually-hidden\">".concat(item.discounted_price, "</span>\n\n          <span data-price=\"").concat(item.discounted_price, "\" js-currency=\"price\">\n            ").concat(formatMoney(item.discounted_price, theme.moneyFormat), "\n          </span>\n\n          <span class=\"visually-hidden\">").concat(item.price, "</span>\n\n          <s data-price=\"").concat(item.original_price, "\" js-currency=\"compareAtPrice\">\n            ").concat(formatMoney(item.original_price, theme.moneyFormat), "\n          </s>\n        ");
      }

      lineItemTemplate += "\n        <span class=\"visually-hidden\">".concat(item.price, "</span>\n\n        <span data-price=\"").concat(item.price, "\" js-currency=\"price\">\n          ").concat(formatMoney(item.price, theme.moneyFormat), "\n        </span>\n\n        <span class=\"visually-hidden\">").concat(item.original_price, "</span>\n        <s>").concat(formatMoney(item.original_price, theme.moneyFormat), "</s>\n      ");
    }

    lineItemTemplate += "\n      <span class=\"\n        body-2\n        ".concat(compareAtPrice > item.price ? 'ajax-cart__price--sale subtitle-2' : '', "\"\n        data-price=\"").concat(item.price, "\"\n        js-currency=\"price\"\n      >\n        ").concat(formatMoney(item.price, theme.moneyFormat), "\n      </span>\n      <div data-product-title=\"{{item.price}}\"></div>\n    ");
    return lineItemTemplate;
  },

  /**
   * Get variant titles.
   * @param {Object} items - Cart line item object data.
   * @returns {String}
   */
  getVariantTitle(item) {
    return this.isVariantTitle(item) ? "<p class=\"ajax-cart__variant-title body-2\">".concat(this.formatVariantTitle(item.options_with_values[0].value), ", ").concat(this.formatVariantTitle(item.options_with_values[1].name), ": ").concat(this.formatVariantTitle(item.options_with_values[1].value), "</p>") : '';
  },

  /**
   * Capitalize first letter of variant title
   */
  formatVariantTitle(title) {
    return title.charAt(0).toUpperCase() + title.slice(1);
  },

  /**
   * Check if current item is an add-on.
   * @param {Object} item - Cart variant item.
   * @returns {Boolean}
   */
  isAddOnItem(item) {
    if (!item) {
      throw Error('Could not find item');
    }

    return item.product_type.toLowerCase() === 'add-on';
  },

  /**
   * Check if vendor title setting is enabled.
   * @returns {Boolean}
   */
  isVendorEnabled() {
    return JSON.parse(this.container.dataset.enableVendorTitle);
  },

  /**
   * Get vendor title template.
   * @param {Object} item - Cart item to get vendor title from.
   * @returns {String}
   */
  getVendorTitle(item) {
    return "<p class=\"ajax-cart__vendor caption\">".concat(item.vendor, "</p>");
  },

  /**
   * Check if variant title exists.
   * @param {Object} item - Cart line item object data.
   * @returns {Boolean}
   */
  isVariantTitle(item) {
    return item.variant_options.length && !item.variant_options[0].includes('Default Title');
  },

  /**
   * Get Preorder message if item has preorder property.
   * @param {Object} item - Cart line item object data.
   * @returns {string} - Template.
   */
  getPreorderMessage(item) {
    const isPreorder = ('_preorder' in item.properties);

    if (!isPreorder) {
      return '';
    }

    return "\n      <div class=\"preorder-notification preorder-notification--ajax-cart\" role=\"alert\">\n        <p js-preorder-notification=\"text\">\n          ".concat(theme.strings.preorder_message, "\n        </p>\n      </div>");
  },

  /**
   * Set the rendering state of the drawer.
   */
  setRenderingState() {
    this.container.classList.add(cssClasses.loading);
  },

  /**
   * Close drawer.
   */
  close() {
    Drawer.close(this.container);
  },

  /**
   * Callback function when section is selected by
   * the Theme Editor `shopify:section:select` event.
   */
  onSelect() {
    Frame.EventBus.emit('AjaxCart:requestUpdate');
  },

  /**
   * Callback function when section is deselected by
   * the Theme Editor `shopify:section:deselect` event.
   */
  onDeselect() {
    this.close();
  },

  /**
   * Callback function when section is loaded via `sections.load()`
   * or by the Theme Editor `shopify:section:load` event.
   */
  onLoad() {
    this.init();
    this.setEventBusListeners();
  }

});